import { useCallback, useEffect, useRef, useState } from 'react';
const GALLERY_RESTORATION_VERSION = 1;
const GALLERY_RESTORATION_SESSION_KEY = 'categoriesNextUi.galleryRestoration';
const GALLERY_RESTORATION_RETURN_KEY = 'categoriesNextUi.restoreGalleryOnNextVisit';
export const MAX_AUTOMATICALLY_RESTORED_GALLERY_PAGES = 10;
export type GalleryRestorationState = {
  version: typeof GALLERY_RESTORATION_VERSION;
  pageCount: number;
  lookId: string | null;
  lookViewportTop: number | null;
  scrollY: number | null;
};
type BrowserHistoryState = Record<string, unknown> & {
  galleryRestoration?: unknown;
};
const DEFAULT_RESTORATION_STATE: GalleryRestorationState = {
  version: GALLERY_RESTORATION_VERSION,
  pageCount: 1,
  lookId: null,
  lookViewportTop: null,
  scrollY: null
};
const normalizePageCount = (pageCount: number) => Math.max(Math.floor(pageCount), 1);
export const parseGalleryRestorationState = (historyState: unknown): GalleryRestorationState => {
  if (!historyState || typeof historyState !== 'object') {
    return DEFAULT_RESTORATION_STATE;
  }
  const restoration = (historyState as BrowserHistoryState).galleryRestoration;
  if (!restoration || typeof restoration !== 'object') {
    return DEFAULT_RESTORATION_STATE;
  }
  const {
    version,
    pageCount,
    lookId,
    lookViewportTop,
    scrollY
  } = restoration as Record<string, unknown>;
  if (version !== GALLERY_RESTORATION_VERSION || typeof pageCount !== 'number' || !Number.isFinite(pageCount) || lookId !== null && typeof lookId !== 'string') {
    return DEFAULT_RESTORATION_STATE;
  }
  return {
    version: GALLERY_RESTORATION_VERSION,
    pageCount: normalizePageCount(pageCount),
    lookId,
    lookViewportTop: typeof lookViewportTop === 'number' && Number.isFinite(lookViewportTop) ? lookViewportTop : null,
    scrollY: typeof scrollY === 'number' && Number.isFinite(scrollY) ? Math.max(Math.floor(scrollY), 0) : null
  };
};
const replaceGalleryRestorationState = (restoration: GalleryRestorationState) => {
  const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
  try {
    window.history.replaceState({
      ...currentState,
      galleryRestoration: restoration
    }, '', window.location.href);
  } catch {
    // Session storage can still support restoration if history is unavailable.
  }
  try {
    window.sessionStorage.setItem(GALLERY_RESTORATION_SESSION_KEY, JSON.stringify(restoration));
  } catch {
    // History state still supports restoration when session storage is blocked.
  }
};
const readSessionRestorationState = () => {
  try {
    const storedRestoration = window.sessionStorage.getItem(GALLERY_RESTORATION_SESSION_KEY);
    return storedRestoration ? parseGalleryRestorationState({
      galleryRestoration: JSON.parse(storedRestoration)
    }) : null;
  } catch {
    return null;
  }
};
const consumeGalleryRestorationReturn = () => {
  try {
    const shouldRestore = window.sessionStorage.getItem(GALLERY_RESTORATION_RETURN_KEY) === 'true';
    window.sessionStorage.removeItem(GALLERY_RESTORATION_RETURN_KEY);
    return shouldRestore;
  } catch {
    return false;
  }
};
export const markGalleryRestorationReturn = (returnUrl: string) => {
  try {
    const returnPath = new URL(returnUrl, window.location.origin).pathname;
    if (returnPath !== '/gallery') return false;
    window.sessionStorage.setItem(GALLERY_RESTORATION_RETURN_KEY, 'true');
    return true;
  } catch {
    return false;
  }
};
export const clearGalleryRestorationReturn = () => {
  try {
    window.sessionStorage.removeItem(GALLERY_RESTORATION_RETURN_KEY);
  } catch {
    // The return marker is best-effort when session storage is unavailable.
  }
};
const readInitialRestorationState = () => {
  const historyState = window.history.state as BrowserHistoryState | null;
  const isGalleryReturn = consumeGalleryRestorationReturn();
  const navigation = window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  if (navigation?.type === 'reload') {
    const sessionRestoration = readSessionRestorationState();
    if (sessionRestoration) return sessionRestoration;
  }
  if (historyState?.galleryRestoration !== undefined) {
    return parseGalleryRestorationState(historyState);
  }
  if (!isGalleryReturn && navigation?.type !== 'reload' && navigation?.type !== 'back_forward') {
    return DEFAULT_RESTORATION_STATE;
  }
  return readSessionRestorationState() ?? DEFAULT_RESTORATION_STATE;
};
export const useGalleryRestoration = () => {
  const [restoration, setRestoration] = useState<GalleryRestorationState>(DEFAULT_RESTORATION_STATE);
  const restorationRef = useRef(DEFAULT_RESTORATION_STATE);
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    const initialRestoration = readInitialRestorationState();
    restorationRef.current = initialRestoration;
    replaceGalleryRestorationState(initialRestoration);
    setRestoration(initialRestoration);
    setIsInitialized(true);
    const recordScrollPosition = () => {
      const next = {
        ...restorationRef.current,
        scrollY: Math.max(Math.floor(window.scrollY), 0)
      };
      restorationRef.current = next;
      replaceGalleryRestorationState(next);
    };
    window.addEventListener('pagehide', recordScrollPosition);
    return () => {
      window.removeEventListener('pagehide', recordScrollPosition);
    };
  }, []);
  const updateRestoration = useCallback((updates: Partial<Pick<GalleryRestorationState, 'pageCount' | 'lookId' | 'lookViewportTop' | 'scrollY'>>, captureScrollPosition = false) => {
    const current = restorationRef.current;
    let scrollY = current.scrollY;
    if (captureScrollPosition) {
      scrollY = Math.max(Math.floor(window.scrollY), 0);
    } else if (updates.scrollY !== undefined) {
      scrollY = updates.scrollY;
    }
    const next = {
      ...current,
      ...updates,
      pageCount: normalizePageCount(updates.pageCount ?? current.pageCount),
      scrollY
    };
    restorationRef.current = next;
    replaceGalleryRestorationState(next);
    setRestoration(next);
  }, []);
  const recordPageCount = useCallback((pageCount: number) => updateRestoration({
    pageCount
  }), [updateRestoration]);
  const recordPageRequest = useCallback((pageCount: number) => updateRestoration({
    pageCount
  }, true), [updateRestoration]);
  const recordLookNavigation = useCallback((lookId: string, lookViewportTop: number) => updateRestoration({
    lookId,
    lookViewportTop
  }, true), [updateRestoration]);
  const clearRestoredPosition = useCallback(() => updateRestoration({
    lookId: null,
    lookViewportTop: null,
    scrollY: null
  }), [updateRestoration]);
  return {
    ...restoration,
    isInitialized,
    recordPageCount,
    recordPageRequest,
    recordLookNavigation,
    clearRestoredPosition
  };
};